import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  DocumentIcon,
  EyeIcon,
  PencilIcon,
  XMarkIcon,
  ChevronLeftIcon,
  DocumentTextIcon,
  IdentificationIcon,
  ArrowDownTrayIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { employeeService } from '../../services/employeeService';
import { employeeDetailsService } from '../../services/employeeDetailsService';
import { useAuth } from '../../context/AuthContext';

const EmployeeDetails = ({ employee, onClose, onEdit }) => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [contract, setContract] = useState(null);
  const [stats, setStats] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch employee data based on active tab
  useEffect(() => {
    if (employee?.id) {
      fetchEmployeeData();
    }
  }, [employee?.id, activeTab]);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      
      switch (activeTab) {
        case 'documents':
          await fetchEmployeeDocuments();
          break;
        case 'performance':
          await fetchEmployeePerformance();
          break;
        case 'attendance':
          await fetchEmployeeAttendance();
          break;
        case 'contract':
          await fetchEmployeeContract();
          break;
        default:
          await fetchEmployeeOverview();
          break;
      }
    } catch (error) {
      console.error('Error fetching employee data:', error);
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDocuments = async () => {
    try {
      const response = await employeeDetailsService.getEmployeeDocuments(employee.id);
      if (response.success) {
        setDocuments(response.data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    }
  };

  const fetchEmployeePerformance = async () => {
    try {
      const response = await employeeDetailsService.getEmployeePerformance(employee.id);
      if (response.success) {
        setPerformance(response.data.reviews || []);
      }
    } catch (error) {
      console.error('Error fetching performance:', error);
      toast.error('Failed to load performance data');
    }
  };

  const fetchEmployeeAttendance = async () => {
    try {
      const response = await employeeDetailsService.getEmployeeAttendance(employee.id);
      if (response.success) {
        setAttendance(response.data.attendanceLogs || []);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Failed to load attendance data');
    }
  };

  const fetchEmployeeContract = async () => {
    try {
      const response = await employeeDetailsService.getEmployeeContract(employee.id);
      if (response.success) {
        setContract(response.data.documents?.[0] || null);
      }
    } catch (error) {
      console.error('Error fetching contract:', error);
      toast.error('Failed to load contract data');
    }
  };

  const fetchEmployeeOverview = async () => {
    try {
      // Fetch stats, leave balance, and emergency contacts
      const [statsResponse, leaveResponse, contactsResponse] = await Promise.all([
        employeeDetailsService.getEmployeeStats(employee.id),
        employeeDetailsService.getEmployeeLeaveBalance(employee.id),
        employeeDetailsService.getEmployeeEmergencyContacts(employee.id)
      ]);

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
      if (leaveResponse.success) {
        setLeaveBalance(leaveResponse.data);
      }
      if (contactsResponse.success) {
        setEmergencyContacts(contactsResponse.data.contacts || []);
      }
    } catch (error) {
      console.error('Error fetching overview data:', error);
      // Don't show error toast for overview as it's not critical
    }
  };

  const handleDownloadDocument = async (documentId, documentName) => {
    try {
      const blob = await employeeDetailsService.downloadEmployeeDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-green-100 text-green-800',
      on_leave: 'bg-yellow-100 text-yellow-800',
      terminated: 'bg-red-100 text-red-800',
      pending_onboarding: 'bg-blue-100 text-blue-800',
      suspended: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getDocumentStatusBadge = (status) => {
    const statusStyles = {
      verified: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      signed: 'bg-blue-100 text-blue-800',
      expired: 'bg-red-100 text-red-800',
      draft: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  const getDocumentIcon = (type) => {
    const icons = {
      contract: DocumentTextIcon,
      identification: IdentificationIcon,
      education: AcademicCapIcon,
      financial: CurrencyDollarIcon,
      personal: UserIcon,
      default: DocumentIcon
    };

    const IconComponent = icons[type] || icons.default;
    return <IconComponent className="h-5 w-5" />;
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: UserIcon },
    { id: 'documents', name: 'Documents', icon: DocumentIcon },
    { id: 'contract', name: 'Contract', icon: DocumentTextIcon },
    { id: 'performance', name: 'Performance', icon: BriefcaseIcon },
    { id: 'attendance', name: 'Attendance', icon: ClockIcon }
  ];

  if (!employee) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 overflow-y-auto h-full w-full z-50">
      {/* Close button overlay */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white bg-opacity-90 hover:bg-opacity-100 shadow-lg transition-all duration-200 hover:scale-110"
        title="Close"
      >
        <XMarkIcon className="h-6 w-6 text-gray-600" />
      </button>
      
      <div className="relative min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <button
                  onClick={onClose}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 mr-4"
                >
                  <ChevronLeftIcon className="h-6 w-6" />
                </button>
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold mr-3">
                    {employee.User?.firstName?.[0]}{employee.User?.fatherName?.[0]}
                  </div>
                  <div>
                    <h1 className="text-xl font-semibold text-gray-900">
                      {employee.User?.firstName} {employee.User?.fatherName} {employee.User?.grandfatherName} {employee.User?.lastName}
                    </h1>
                    <p className="text-sm text-gray-500">{employee.jobTitle} • {employee.Department?.name}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                {hasPermission('employees.update') && (
                  <button
                    onClick={() => onEdit(employee)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white min-h-screen">
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-8 bg-white sticky top-16 z-5">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <IconComponent className="h-4 w-4 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Personal Information */}
                <div className="lg:col-span-2">
                                     <div className="bg-white shadow-lg rounded-lg border border-gray-200">
                     <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                       <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
                     </div>
                    <div className="px-6 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Full Name</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {employee.User?.firstName} {employee.User?.fatherName} {employee.User?.grandfatherName} {employee.User?.lastName}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Email</label>
                          <p className="mt-1 text-sm text-gray-900 flex items-center">
                            <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                            {employee.User?.email}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Phone</label>
                          <p className="mt-1 text-sm text-gray-900 flex items-center">
                            <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                            {employee.phoneNumber || 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Address</label>
                          <p className="mt-1 text-sm text-gray-900 flex items-center">
                            <MapPinIcon className="h-4 w-4 mr-2 text-gray-400" />
                            {employee.address || 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Date of Birth</label>
                          <p className="mt-1 text-sm text-gray-900 flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                            {employee.dateOfBirth ? new Date(employee.dateOfBirth).toLocaleDateString() : 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Nationality</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {employee.nationality || 'Not provided'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                                     {/* Employment Information */}
                   <div className="bg-white shadow-lg rounded-lg border border-gray-200 mt-6">
                     <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                       <h3 className="text-lg font-medium text-gray-900">Employment Information</h3>
                     </div>
                    <div className="px-6 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Employee ID</label>
                          <p className="mt-1 text-sm text-gray-900">{employee.employeeId}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Employee Number</label>
                          <p className="mt-1 text-sm text-gray-900">{employee.employeeNumber}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Job Title</label>
                          <p className="mt-1 text-sm text-gray-900">{employee.jobTitle}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Department</label>
                          <p className="mt-1 text-sm text-gray-900 flex items-center">
                            <BuildingOfficeIcon className="h-4 w-4 mr-2 text-gray-400" />
                            {employee.Department?.name}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Employment Type</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {employee.employmentType?.replace('_', ' ').toUpperCase()}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Status</label>
                          <div className="mt-1">
                            {getStatusBadge(employee.status)}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Join Date</label>
                          <p className="mt-1 text-sm text-gray-900 flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                            {employee.joinDate ? new Date(employee.joinDate).toLocaleDateString() : 'Not specified'}
                          </p>
                        </div>
                                                 <div>
                           <label className="block text-sm font-medium text-gray-500">Manager</label>
                           <p className="mt-1 text-sm text-gray-900">
                             {employee.manager?.User?.firstName && employee.manager?.User?.lastName 
                               ? `${employee.manager.User.firstName} ${employee.manager.User.lastName}` 
                               : 'Not assigned'}
                           </p>
                         </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="space-y-6">
                                     <div className="bg-white shadow-lg rounded-lg border border-gray-200 p-6">
                     <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Days Employed</span>
                        <span className="text-sm font-medium text-gray-900">
                          {employee.joinDate ? Math.floor((new Date() - new Date(employee.joinDate)) / (1000 * 60 * 60 * 24)) : 0}
                        </span>
                      </div>
                                             <div className="flex items-center justify-between">
                         <span className="text-sm text-gray-500">Leave Balance</span>
                         <span className="text-sm font-medium text-gray-900">
                           {leaveBalance?.totalDays || 0} days
                         </span>
                       </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Documents</span>
                        <span className="text-sm font-medium text-gray-900">{documents.length}</span>
                      </div>
                                             <div className="flex items-center justify-between">
                         <span className="text-sm text-gray-500">Performance Rating</span>
                         <span className="text-sm font-medium text-green-600">
                           {stats?.averageRating ? `${stats.averageRating}/5.0` : 'N/A'}
                         </span>
                       </div>
                    </div>
                  </div>

                                     <div className="bg-white shadow-lg rounded-lg border border-gray-200 p-6">
                     <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
                     {emergencyContacts.length > 0 ? (
                       <div className="space-y-3">
                         {emergencyContacts.map((contact, index) => (
                           <div key={index}>
                             <label className="block text-xs font-medium text-gray-500">Name</label>
                             <p className="text-sm text-gray-900">{contact.name}</p>
                             <div className="mt-1">
                               <label className="block text-xs font-medium text-gray-500">Relationship</label>
                               <p className="text-sm text-gray-900">{contact.relationship}</p>
                             </div>
                             <div className="mt-1">
                               <label className="block text-xs font-medium text-gray-500">Phone</label>
                               <p className="text-sm text-gray-900">{contact.phone}</p>
                             </div>
                           </div>
                         ))}
                       </div>
                     ) : (
                       <p className="text-sm text-gray-500">No emergency contacts found</p>
                     )}
                   </div>
                </div>
              </div>
            )}

                         {activeTab === 'documents' && (
               <div className="bg-white shadow-lg rounded-lg border border-gray-200">
                 <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Employee Documents</h3>
                    <button className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      <DocumentIcon className="h-4 w-4 mr-2" />
                      Upload Document
                    </button>
                  </div>
                </div>
                <div className="px-6 py-4">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {documents.map((document) => (
                        <div key={document.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                {getDocumentIcon(document.type)}
                              </div>
                            </div>
                            <div className="ml-4">
                              <h4 className="text-sm font-medium text-gray-900">{document.name}</h4>
                              <p className="text-sm text-gray-500">{document.category} • {document.fileSize}</p>
                              <p className="text-xs text-gray-400">Uploaded {document.uploadedAt}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getDocumentStatusBadge(document.status)}
                            <button className="p-1 text-gray-400 hover:text-gray-600">
                              <EyeIcon className="h-4 w-4" />
                            </button>
                                                         <button 
                               onClick={() => handleDownloadDocument(document.id, document.name)}
                               className="p-1 text-gray-400 hover:text-gray-600"
                               title="Download"
                             >
                               <ArrowDownTrayIcon className="h-4 w-4" />
                             </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

                         {activeTab === 'contract' && (
               <div className="bg-white shadow-lg rounded-lg border border-gray-200">
                 <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                   <h3 className="text-lg font-medium text-gray-900">Employment Contract</h3>
                 </div>
                 <div className="px-6 py-4">
                   {contract ? (
                     <>
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                         <div>
                           <h4 className="text-md font-medium text-gray-900 mb-4">Contract Details</h4>
                           <div className="space-y-4">
                             <div>
                               <label className="block text-sm font-medium text-gray-500">Contract Type</label>
                               <p className="mt-1 text-sm text-gray-900">{contract.contractType || 'Not specified'}</p>
                             </div>
                             <div>
                               <label className="block text-sm font-medium text-gray-500">Start Date</label>
                               <p className="mt-1 text-sm text-gray-900">
                                 {contract.startDate ? new Date(contract.startDate).toLocaleDateString() : 'Not specified'}
                               </p>
                             </div>
                             <div>
                               <label className="block text-sm font-medium text-gray-500">End Date</label>
                               <p className="mt-1 text-sm text-gray-900">
                                 {contract.endDate ? new Date(contract.endDate).toLocaleDateString() : 'Indefinite'}
                               </p>
                             </div>
                             <div>
                               <label className="block text-sm font-medium text-gray-500">Probation Period</label>
                               <p className="mt-1 text-sm text-gray-900">{contract.probationPeriod || 'Not specified'}</p>
                             </div>
                             <div>
                               <label className="block text-sm font-medium text-gray-500">Notice Period</label>
                               <p className="mt-1 text-sm text-gray-900">{contract.noticePeriod || 'Not specified'}</p>
                             </div>
                           </div>
                         </div>
                         <div>
                           <h4 className="text-md font-medium text-gray-900 mb-4">Compensation</h4>
                           <div className="space-y-4">
                             <div>
                               <label className="block text-sm font-medium text-gray-500">Base Salary</label>
                               <p className="mt-1 text-sm text-gray-900">{contract.baseSalary || 'Not specified'}</p>
                             </div>
                             <div>
                               <label className="block text-sm font-medium text-gray-500">Allowances</label>
                               <p className="mt-1 text-sm text-gray-900">{contract.allowances || 'Not specified'}</p>
                             </div>
                             <div>
                               <label className="block text-sm font-medium text-gray-500">Benefits</label>
                               <p className="mt-1 text-sm text-gray-900">{contract.benefits || 'Not specified'}</p>
                             </div>
                             <div>
                               <label className="block text-sm font-medium text-gray-500">Working Hours</label>
                               <p className="mt-1 text-sm text-gray-900">{contract.workingHours || 'Not specified'}</p>
                             </div>
                           </div>
                         </div>
                       </div>
                       
                       <div className="mt-8">
                         <h4 className="text-md font-medium text-gray-900 mb-4">Contract Status</h4>
                         <div className="flex items-center space-x-4">
                           <div className="flex items-center">
                             <ShieldCheckIcon className="h-5 w-5 text-green-500 mr-2" />
                             <span className="text-sm text-gray-900">{contract.status || 'Active'}</span>
                           </div>
                           <div className="flex items-center">
                             <DocumentTextIcon className="h-5 w-5 text-blue-500 mr-2" />
                             <span className="text-sm text-gray-900">{contract.signedStatus || 'Signed'}</span>
                           </div>
                           <div className="flex items-center">
                             <CalendarIcon className="h-5 w-5 text-gray-500 mr-2" />
                             <span className="text-sm text-gray-900">
                               Valid until: {contract.validUntil || 'Indefinite'}
                             </span>
                           </div>
                         </div>
                       </div>
                     </>
                   ) : (
                     <div className="text-center py-8">
                       <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                       <h3 className="mt-2 text-sm font-medium text-gray-900">No contract found</h3>
                       <p className="mt-1 text-sm text-gray-500">
                         No employment contract has been uploaded for this employee.
                       </p>
                     </div>
                   )}
                 </div>
               </div>
             )}

                         {activeTab === 'performance' && (
               <div className="bg-white shadow-lg rounded-lg border border-gray-200">
                 <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-lg font-medium text-gray-900">Performance Overview</h3>
                </div>
                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-900">Overall Rating</h4>
                      <p className="text-2xl font-bold text-blue-600">4.2/5.0</p>
                      <p className="text-xs text-blue-600">Excellent</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-green-900">Goals Achieved</h4>
                      <p className="text-2xl font-bold text-green-600">85%</p>
                      <p className="text-xs text-green-600">On track</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-purple-900">Last Review</h4>
                      <p className="text-2xl font-bold text-purple-600">3 months ago</p>
                      <p className="text-xs text-purple-600">Due in 1 month</p>
                    </div>
                  </div>
                  
                                     <div className="space-y-4">
                     <h4 className="text-md font-medium text-gray-900">Recent Reviews</h4>
                     <div className="space-y-3">
                       {performance.length > 0 ? (
                         performance.map((review) => (
                           <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                             <div className="flex items-center justify-between mb-2">
                               <h5 className="text-sm font-medium text-gray-900">{review.reviewPeriod}</h5>
                               <span className="text-sm text-gray-500">
                                 {new Date(review.reviewDate).toLocaleDateString()}
                               </span>
                             </div>
                             <p className="text-sm text-gray-600 mb-2">
                               {review.comments || 'No comments provided'}
                             </p>
                             <div className="flex items-center">
                               <span className="text-sm font-medium text-gray-900 mr-2">Rating:</span>
                               <span className="text-sm text-green-600">{review.rating}/5.0</span>
                             </div>
                           </div>
                         ))
                       ) : (
                         <p className="text-sm text-gray-500">No performance reviews found</p>
                       )}
                     </div>
                   </div>
                </div>
              </div>
            )}

                         {activeTab === 'attendance' && (
               <div className="bg-white shadow-lg rounded-lg border border-gray-200">
                 <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-lg font-medium text-gray-900">Attendance Overview</h3>
                </div>
                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-green-900">Present Days</h4>
                      <p className="text-2xl font-bold text-green-600">18</p>
                      <p className="text-xs text-green-600">This month</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-yellow-900">Leave Days</h4>
                      <p className="text-2xl font-bold text-yellow-600">2</p>
                      <p className="text-xs text-yellow-600">This month</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-red-900">Absent Days</h4>
                      <p className="text-2xl font-bold text-red-600">0</p>
                      <p className="text-xs text-red-600">This month</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-900">Attendance Rate</h4>
                      <p className="text-2xl font-bold text-blue-600">90%</p>
                      <p className="text-xs text-blue-600">This month</p>
                    </div>
                  </div>
                  
                                     <div className="space-y-4">
                     <h4 className="text-md font-medium text-gray-900">Recent Attendance</h4>
                     <div className="space-y-2">
                       {attendance.length > 0 ? (
                         attendance.slice(0, 7).map((log) => (
                           <div key={log.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                             <div className="flex items-center">
                               <CalendarIcon className="h-4 w-4 text-gray-400 mr-3" />
                               <span className="text-sm text-gray-900">
                                 {new Date(log.timestamp).toLocaleDateString('en-US', { 
                                   weekday: 'short', 
                                   month: 'short', 
                                   day: 'numeric' 
                                 })}
                               </span>
                             </div>
                             <div className="flex items-center space-x-2">
                               <span className="text-sm text-gray-500">
                                 {new Date(log.timestamp).toLocaleTimeString('en-US', { 
                                   hour: '2-digit', 
                                   minute: '2-digit' 
                                 })}
                               </span>
                               <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                 log.type === 'clock_in' ? 'bg-green-100 text-green-800' :
                                 log.type === 'clock_out' ? 'bg-red-100 text-red-800' :
                                 'bg-gray-100 text-gray-800'
                               }`}>
                                 {log.type.replace('_', ' ').toUpperCase()}
                               </span>
                             </div>
                           </div>
                         ))
                       ) : (
                         <p className="text-sm text-gray-500">No attendance records found</p>
                       )}
                     </div>
                   </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetails; 